<template>
	<div class="v-map">
		<l-map
			ref="map"
			:maxBounds="localMaxBounds"
			:zoom.sync="localZoom"
			:center.sync="localCenter"
			:bounds.sync="localBounds"
			class="v-map__wrapper"
		>
			<l-control-layers position="topright"></l-control-layers>
			<l-tile-layer
				v-for="tileProvider in tileProviders"
				:key="tileProvider.name"
				:name="tileProvider.name"
				:visible="tileProvider.visible"
				:url="tileProvider.url"
				:attribution="tileProvider.attribution"
				layer-type="base"
			/>
		
			<l-layer-group>
				<l-marker
					v-for="(latlag, index) in polylinePoints"
					:key="index"
					:lat-lng.sync="polylineLatlngs[index]"
					:draggable="true"
					@update:lat-lng="$emit('update:polyline-points', polylineLatlngs)"
					@contextmenu="menu.active"
				></l-marker>
				<l-polyline :lat-lngs="polylinePoints" color="#ccc" :weight="8"></l-polyline>
				<l-polyline :lat-lngs="polylinePoints" color="#fff" :weight="6"></l-polyline>
				<l-polyline :lat-lngs="polylinePoints" :color="polylineColor" :weight="2"></l-polyline>
			</l-layer-group>

			<l-marker
				v-for="(point, index) in pointsLatlngs"
				:key="index"
				:lat-lng="point.latLng ? point.latLng : point"
				:draggable="point.draggable"
				@update:lat-lng="
					point.latLng
						? pointsLatlngs[index].latLng = $event
						: pointsLatlngs[index] = $event;
					$emit('update:points', pointsLatlngs);
				"
				@contextmenu="menu.active"
			>
				<l-icon
					v-if="!Array.isArray(point) && point.icon"
					:icon-size="point.icon.size || [30, 30]"
					:icon-anchor="point.icon.anchor || [18, 18]"
					:icon-url="point.icon.url"
				></l-icon>
				<l-popup v-if="point.popupText">{{point.popupText}}</l-popup>
			</l-marker>

			<l-control position="bottomleft">
				<slot name="bottom-left-area"></slot>
			</l-control>
			<l-control position="bottomright">
				<slot name="bottom-right-area"></slot>
			</l-control>
		</l-map>

		<!-- loader -->
		<v-progress-linear
			v-if="loading || routing.loading"
			indeterminate
			height="2"
			color="primary"
			class="mx-auto"
		/>

		<!-- menu -->
		<v-menu
			open-delay="0"
			v-if="contextMenuItems.length"
			v-model="menu.show"
			:position-x="menu.x"
			:position-y="menu.y"
			absolute
			offset-y
		>
			<v-list dense>
				<v-list-item
					v-for="(item, index) in contextMenuItems"
					:key="index"
					:class="item.class"
					@click="$emit(`context-menu-${item.action}`, menu.coordinates)"
				>
					<v-list-item-icon class="me-3">
						<v-icon :color="item.iconColor">{{item.icon}}</v-icon>
					</v-list-item-icon>
					<v-list-item-title>{{item.text}}</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>
	</div>
</template>

<script>
import L, { latLngBounds } from 'leaflet'
import { LMap, LTileLayer, LMarker, LControl, LControlLayers, LLayerGroup, LPolyline, LIcon, LPopup } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import 'leaflet-routing-machine';
// import '@/helpers/open-routes-service.js'
import '@gegeweb/leaflet-routing-machine-openroute'

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
	iconUrl: require('leaflet/dist/images/marker-icon.png'),
	shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default {
	name: 'VMap',

	props: {
		loading: { type: Boolean, default: false },
		points: { type: Array, default: () => ([]) },
		polylinePoints: { type: Array, default: () => ([]) },
		polylineColor: { type: String, default: '#42ab59' },
		routingLineColor: { type: String, default: '#a149ca' },
		routingWhileDragging: { type: Boolean, default: false },
		center: { type: [Object, Array], default: () => ({ lat: 36.209628, lng: 37.126654 }) },
		zoom: { type: Number, default: 16 },
		bounds: { type: Array },
		contextMenuItems: { type: Array, default: () => ([]) },
		maxBounds: {
			type: Object,
			default: () => (latLngBounds([
				[32.325653, 35.518799],
				[37.26362, 42.286377]
			]))
		},
	},

	components: {
		LMap,
		LTileLayer,
		LMarker,
		LControl,
		LControlLayers,
		LLayerGroup,
		LPolyline,
		LIcon,
		LPopup,
	},
	
	data() {
		return {
			polylineLatlngs: [],
			pointsLatlngs: [],

			tileProviders: [
				{
					name: 'Google Maps',
					visible: true,
					url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
				},
				{
					name: 'Google Satellite',
					visible: false,
					url: 'https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
				},
				{
					name: 'Open street',
					visible: false,
					url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
				}
			],

			routing: {
				loading: false,
				control: {},
				updatePolylinePoints: [],
				waypoints: []
			},

			menu: {
				show: null,
				x: null,
				y: null,
				coordinates: null,
				active: (e) => {
					this.menu.x = e.originalEvent.clientX;
					this.menu.y = e.originalEvent.clientY;
					this.menu.coordinates = e.latlng;
					this.menu.show = true;
				}
			}
		};
	},

	watch: {
		polylinePoints: {
			handler: function (newVal) {
				this.polylineLatlngs = newVal;
			},
			immediate: true
		},
		points: {
			handler: function (newVal) {
				this.pointsLatlngs = newVal;
			},
			immediate: true
		}
	},

	computed: {
		localCenter: {
			get() {
				return this.center;
			},
			set(newValue) {
				this.$emit('update:center', newValue)
			}
		},
		localZoom: {
			get() {
				return this.zoom;
			},
			set(newVal) {
				this.$emit('update:zoom', newVal);
			}
		},
		localBounds: {
			get() {
				return this.bounds;
			},
			set(newVal) {
				this.$emit('update:bounds', newVal);
			}
		},
		localMaxBounds: {
			get() {
				return this.maxBounds;
			},
			set(newVal) {
				this.$emit('update:maxBounds', newVal);
			}
		},
	},

	methods: {
		setViewTo(coordinates) {
			this.$refs.map.mapObject.setView(coordinates, this.zoom, this.$refs.map.mapObject.options);
		},

		activateRouting(points) {
			// npm install vue2-leaflet-routing-machine
			// <l-routing-machine :waypoints="waypoints" />
			this.routing.control = L.Routing.control({
				router: new L.Routing.OpenRouteService(
					// API key for https://api.openrouteservice.org
					'5b3ce3597851110001cf6248908fad5a24e54720970b43deac7219e1',
					{
						profile: 'driving-car',
						geometry_simplify: true, // Needs to do _decodePolyline and smaller request
						routingQueryParams: {
							attributes: [
								'avgspeed',
								'percentage'
							],
							language: 'en-US',
							maneuvers: true,
							preference: 'recommended',
						}
					}
				),
				waypoints: points,
				routeWhileDragging: this.routingWhileDragging,
				show: false,
				position: 'bottomright',
				collapsible: true,
				collapseBtnClass: 'leaflet-routing-collapse-btn',
				containerClassName: 'container-routing',
				alternativeClassName: 'alternative-panel',
				fitSelectedRoutes: 'smart',

				pointMarkerStyle: {
					radius: 5,
					color: '#3392ff',
					fillColor: '#3392ff',
					opacity: 1,
					fillOpacity: 0.3
				},
				lineOptions: {
					styles: [
						{ color: 'black', opacity: 0.15, weight: 9 },
						{ color: 'white', opacity: 0.8, weight: 6 },
						{ color: this.routingLineColor, opacity: 1, weight: 2 }
					],
					addWaypoints: false,
					missingRouteStyles: [
						{ color: 'black', opacity: 0.15, weight: 7 },
						{ color: 'white', opacity: 0.6, weight: 4 },
						{ color: 'gray', opacity: 0.8, weight: 2, dashArray: '7,12' }
					],
					title: 'Route',
					alt: 'Route',
				}
			}).on('routingstart', (e) => {
				this.routing.loading = true;
			}).on('routesfound', (response) => {
				this.routing.loading = false;
				this.routing.updatePolylinePoints = response.waypoints.map(c => c.latLng);
				this.routing.waypoints = response.routes[0].coordinates;
				this.polylineLatlngs = [];
				this.$emit('update:polyline-points', []);
			}).on('routingerror', (e) => {
				this.routing.loading = false;
				console.error(e);
			}).addTo(this.$refs.map.mapObject);
		},

		removeRouting() {
			this.routing.control.remove();
		},

		getWaypoints(updatePointOnly) {
			return updatePointOnly ? this.routing.updatePolylinePoints : this.routing.waypoints;
		}
	},

	mounted() {
		this.$refs.map.mapObject.on('contextmenu', (e) => this.menu.active(e));
	},

	beforeDestroy() {
		this.$refs.map.mapObject.off('contextmenu', (e) => this.menu.active(e));
	}
};
</script>

<style lang="scss">
.v-map {
	height: 100%;

	&__wrapper {
		z-index: 0;
	}
	.leaflet-control-attribution {
		display: none;
	}
	
	.leaflet-routing-collapse-btn {
		color: #333;
	}
	.container-routing {
		&.leaflet-routing-container-hide {
			margin-bottom: 0 !important;
			width: 45px;
			height: 45px;
			padding: 2px;
			
			.leaflet-routing-collapse-btn {
				cursor: pointer;
				top: 20%;
			}
		}
	}
	.alternative-panel {
		padding-top: 20px;
	}
}
</style>